import styled from "styled-components"

import { Banner, colors } from "@minuthq/meatball-ui-react"
import { Text, TextProps } from "@minuthq/meatball-ui-react"

import { useTranslate } from "src/i18n/useTranslate"
import { MCircularProgress } from "src/ui/MCircularProgress/MCircularProgress"
import { spacing } from "src/ui/spacing"

const CHART_HEIGHT = 450 //px

export type GraphCardProps = {
  title?: React.ReactNode
  titleVariant?: TextProps["variant"]
  info?: React.ReactNode
  rightInfo?: React.ReactNode
  isLoading?: boolean
  isError?: boolean
  children: React.ReactNode
  hideGraphBorder?: boolean
  headerPadding?: string
  chartPadding?: string
  hidden?: boolean
  isLegacyGraph?: boolean
}

export function GraphCard({
  title,
  info,
  rightInfo,
  isLoading,
  isError,
  children,
  hideGraphBorder,
  headerPadding,
  chartPadding,
  hidden,
  isLegacyGraph,
  titleVariant = "subtitle",
}: GraphCardProps) {
  const { t, langKeys } = useTranslate()

  if (hidden) {
    return null
  }

  return (
    <Card $hideGraphBorder={hideGraphBorder}>
      <GraphHeader $hideGraphBorder={hideGraphBorder} $padding={headerPadding}>
        <div>
          <Text variant={titleVariant}>{title}</Text>
          <Text variant="bodyS" color="secondary">
            {info}
          </Text>
        </div>
        <Text variant="heading1" contentType="number" color="primary">
          {rightInfo}
        </Text>
      </GraphHeader>

      <Chart $isLegacyGraph={!!isLegacyGraph} $padding={chartPadding}>
        {isLoading ? (
          <CenterBox>
            <MCircularProgress />
          </CenterBox>
        ) : isError ? (
          <CenterBox>
            <Banner type="error">
              {t(langKeys.failed_general_error_try_refreshing_page)}
            </Banner>
          </CenterBox>
        ) : (
          children
        )}
      </Chart>
    </Card>
  )
}

const Card = styled.div<{ $hideGraphBorder?: boolean }>`
  ${({ $hideGraphBorder: hideGraphBorder }) =>
    !hideGraphBorder && `border: 1px solid ${colors.divider}`};
  border-radius: 16px;
`

const GraphHeader = styled.div<{
  $hideGraphBorder?: boolean
  $padding?: string
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${({ $hideGraphBorder: hideGraphBorder, $padding: padding }) =>
    padding
      ? `padding: ${padding};`
      : hideGraphBorder
        ? `padding: 0 ${spacing.L} 0;`
        : `padding: ${spacing.L} ${spacing.L} 0;`};
`

const Chart = styled.div<{ $isLegacyGraph: boolean; $padding?: string }>`
  padding-right: ${spacing.XL};
  padding-left: ${spacing.XL};
  ${({ $isLegacyGraph, $padding: padding }) => {
    if ($isLegacyGraph) {
      return `height: ${CHART_HEIGHT}px;`
    }
    return padding ? `padding: ${padding};` : `padding: ${spacing.L};`
  }}
`

const CenterBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`
